import Handsontable from 'handsontable/base';
import { BasePlugin } from 'handsontable/plugins';
import type { GridSettings } from 'handsontable/settings';
import { isNumber } from 'lodash';
import type { MutableRefObject } from 'react';
import { useEffect, useState } from 'react';
import { TableDataVersion, type CellEditingSession, type TableContentData } from '../../../common/table/TableTypes';
import type { MilanoteEditorRef } from '../../components/editor/milanoteEditor/MilanoteEditorTypes';
import getClientConfig from '../../utils/getClientConfig';
import { FormulaDropdownRef } from './FormulaDropdown';
import type { UseTableCellEditingHandlersReturn } from './handlers/useTableCellEditingHandlers';
import type { UseTableContextMenuReturn } from './handlers/useTableContextMenu';
import type { UseTableMoveColumnRowHandlersReturn } from './handlers/useTableMoveColumnRowHandlers';
import type { UseTableSelectionHandlersReturn } from './handlers/useTableSelectionHandlers';
import hyperFormulaInstance from './manager/hyperFormulaInstance';
import ContextMenuExtensionHotPlugin, {
    CONTEXT_MENU_EXTENSION_PLUGIN_NAME,
} from './plugins/ContextMenuExtensionHotPlugin';
import { EDITING_CLEANUP_PLUGIN_NAME } from './plugins/EditingCleanupHotPlugin';
import { EDITING_PLUGIN_NAME } from './plugins/EditingHotPlugin';
import { FORMULA_AUTOCOMPLETE_PLUGIN_NAME } from './plugins/FormulaAutocompleteHotPlugin';
import { FORMULA_CELL_REFERENCE_PLUGIN_NAME } from './plugins/FormulaCellReferenceHotPlugin';
import { CONTEXT_MENU_SHORTCUTS_PLUGIN_NAME } from './plugins/ContextMenuShortcutsHotPlugin';
import { EDITOR_SHORTCUTS_PLUGIN_NAME } from './plugins/EditorShortcutsHotPlugin';
import { COPY_PASTE_EXTENSION_PLUGIN_NAME } from './plugins/CopyPasteExtensionHotPlugin';
import { GRID_SHORTCUTS_PLUGIN_NAME } from './plugins/GridShortcutsHotPlugin';
import { GRID_UPDATE_PLUGIN_NAME } from './plugins/GridUpdateHotPlugin';
import { FORMULA_TYPE_INFERENCE_PLUGIN_NAME } from './plugins/FormulaTypeInferenceHotPlugin';
import type { MNHotInstance } from './tableHotInstanceTypes';
import { sanitizeClipboardHtmlForPaste } from './utils/dataUtils/clipboard/tableClipboardUtils';
import { convertColWidthsGUtoPx } from './utils/dataUtils/columnSize/tableColWidthConversionUtils';
import { getCellValueArray } from './utils/dataUtils/persistence/tablePersistenceValueUtils';
import { getColWidthsInList } from './utils/hotInstanceUtils/columnSize/hotTableColWidthInListUtils';
import { getDefaultTableColWidth } from './utils/hotInstanceUtils/grid/hotTableGridDefaultDimensionUtils';
import { applyRawTableContentToHotInstance } from './utils/hotInstanceUtils/persistence/hotTableRehydrationUtils';
import { removeHyperFormulaUndoRedoEntriesForSheet } from './utils/hotInstanceUtils/undoRedo/hotTableUndoRedoHyperFormulaUtils';
import useSyncHotMilanoteProps from './handlers/useSyncHotMilanoteProps';
import { UndoRedoRichCellDataState } from './utils/hotInstanceUtils/undoRedo/hotTableUndoRedoRichCellDataUtils';
import { DRAFTJS_FORMULA_CELL_REFERENCE_PLUGIN_NAME } from './plugins/FormulaCellReferenceHotPlugin/DraftJSFormulaCellReferenceHotPlugin';
import { DRAFTJS_FORMULA_AUTOCOMPLETE_PLUGIN_NAME } from './plugins/DraftJSFormulaAutocompleteHotPlugin';

const config = getClientConfig();

type HandsontableHook<Key extends keyof GridSettings> = NonNullable<GridSettings[Key]>;

type HotTableRendererPortalManagerRef = {
    getRendererWrapper: () => HandsontableHook<'renderer'>;
    afterViewRender: HandsontableHook<'afterViewRender'>;
};

// Handsontable doesn't seem to expose these types
type MilanoteCopyPasteFocusableElement = {
    mainElement: {
        value: string;
        select: () => void;
    };
    focus?: (this: MilanoteCopyPasteFocusableElement) => void;
};

type HotTableCopyPastePlugin = BasePlugin & {
    focusableElement?: MilanoteCopyPasteFocusableElement;
};

type UseInitializeHotTableArgs = {
    elementId: string;
    tableDataVersion: TableDataVersion;
    tableContentData: TableContentData;
    tableContentColWidthsGU: number[];
    hotTableInstanceRef: MutableRefObject<MNHotInstance | null>;
    hotTableContainerRef: MutableRefObject<HTMLDivElement | null>;
    hotTableRendererPortalManagerRef: MutableRefObject<HotTableRendererPortalManagerRef | null>;
    gridSize: number;
    isDarkMode: boolean;
    inList?: string;
    isReadOnly: boolean;
    isResizing: boolean;
    getContextZoomScale: () => number;
    showTitle: boolean;
    showBottomPanel: boolean;
    locale: string;
    startEditingCellEditor: (cellEditingSession: CellEditingSession) => void;
    finishEditingCellEditor: () => void;
    tableCellEditingHandlers: UseTableCellEditingHandlersReturn;
    tableCellSelectionHandlers: UseTableSelectionHandlersReturn;
    tableMoveColumnRowHandlers: UseTableMoveColumnRowHandlersReturn;
    tableContextMenuHandlers: UseTableContextMenuReturn;
    jumpToCaption: () => void;
    jumpToTitle: () => void;
    cellEditorRef: MutableRefObject<MilanoteEditorRef | null>;
    undoRedoRichCellDataState: UndoRedoRichCellDataState;
    dropdownRef: MutableRefObject<FormulaDropdownRef | null>;
};

type PluginSettings = {
    [EDITING_PLUGIN_NAME]: boolean;
    [GRID_SHORTCUTS_PLUGIN_NAME]: boolean;
    [EDITOR_SHORTCUTS_PLUGIN_NAME]: boolean;
    [CONTEXT_MENU_SHORTCUTS_PLUGIN_NAME]: boolean;
    [EDITING_CLEANUP_PLUGIN_NAME]: boolean;
    // TODO-TABLES: Make non-optional once DraftJS is fully deprecated
    [FORMULA_CELL_REFERENCE_PLUGIN_NAME]?: boolean;
    [FORMULA_AUTOCOMPLETE_PLUGIN_NAME]?: {
        dropdownRef: MutableRefObject<FormulaDropdownRef | null>;
    };
    [CONTEXT_MENU_EXTENSION_PLUGIN_NAME]: boolean;
    [COPY_PASTE_EXTENSION_PLUGIN_NAME]: boolean;
    [GRID_UPDATE_PLUGIN_NAME]: boolean;
    [FORMULA_TYPE_INFERENCE_PLUGIN_NAME]: boolean;

    // TODO-TABLES: Remove DraftJS plugins once DraftJS is fully deprecated
    [DRAFTJS_FORMULA_CELL_REFERENCE_PLUGIN_NAME]?: boolean;
    [DRAFTJS_FORMULA_AUTOCOMPLETE_PLUGIN_NAME]?: {
        dropdownRef: MutableRefObject<FormulaDropdownRef | null>;
    };
};

type MilanoteGridSettings = GridSettings & PluginSettings;

/**
 * Creates the Handsontable config used for the table instance lifecycle.
 */
const createHotTableSettings = ({
    elementId,
    tableContentData,
    tableContentColWidthsGU,
    hotTableContainerRef,
    hotTableRendererPortalManagerRef,
    gridSize,
    inList,
    isReadOnly,
    getContextZoomScale,
    tableCellEditingHandlers,
    tableCellSelectionHandlers,
    tableMoveColumnRowHandlers,
    tableContextMenuHandlers,
    dropdownRef,
    tableDataVersion,
}: UseInitializeHotTableArgs): MilanoteGridSettings => {
    const resizeToWidth = getColWidthsInList(inList, hotTableContainerRef, getContextZoomScale);
    const tableContentColWidthsPx = convertColWidthsGUtoPx(tableContentColWidthsGU, gridSize, resizeToWidth);

    const isDraftJs = tableDataVersion !== TableDataVersion.V3;
    const cellEditor = isDraftJs ? 'draftCellEditor' : 'tiptapCellEditor';

    return {
        licenseKey: config.handsontable?.licenseKey,
        data: getCellValueArray(tableContentData),
        editor: isReadOnly ? undefined : cellEditor,
        renderer: hotTableRendererPortalManagerRef.current?.getRendererWrapper(),
        colHeaders: true,
        rowHeaders: true,
        autoColumnSize: false,
        autoRowSize: false,
        autoWrapRow: true,
        manualRowMove: true,
        manualColumnMove: true,
        columnHeaderHeight: 0,
        height: 'auto',
        // @ts-ignore I assume that the type here is incorrect? Sheet name has been there since Aug 2023 - 32ee04d7
        formulas: { engine: hyperFormulaInstance, sheetName: elementId },
        manualColumnResize: tableContentColWidthsPx,
        contextMenu: tableContextMenuHandlers.getTableContextMenuConfig,
        outsideClickDeselects: false,
        colWidths: () => getDefaultTableColWidth(gridSize),
        sanitizer: sanitizeClipboardHtmlForPaste,
        // NOTE: Needs to be in the function format to avoid the `this` context being lost
        afterInit(this: MNHotInstance) {
            // This is a hack to get the table to get handsontable context menu event only triggered when there are
            // cell selections
            const milanoteContextMenuPlugin = this.getPlugin(CONTEXT_MENU_EXTENSION_PLUGIN_NAME) as
                | ContextMenuExtensionHotPlugin
                | undefined;
            milanoteContextMenuPlugin?.removeHotContextMenuEventListeners();

            // This is a hack to force handsontable to open keyboard on cell selection on the iPad.
            // Handsontable, by default, does not do this: https://github.com/handsontable/handsontable/pull/5529
            const copyPastePluginInstance = this.getPlugin('copyPaste') as HotTableCopyPastePlugin | undefined;
            if (copyPastePluginInstance?.focusableElement?.focus && !isReadOnly) {
                copyPastePluginInstance.focusableElement.focus = function (this: MilanoteCopyPasteFocusableElement) {
                    this.mainElement.value = ' ';
                    this.mainElement.select();
                };
            }
        },
        afterViewRender: (...args) => hotTableRendererPortalManagerRef.current?.afterViewRender(...args),
        beforeChange: tableCellEditingHandlers.beforeChange,
        beforeOnCellMouseDown: (...args) => {
            tableCellEditingHandlers.beforeOnCellMouseDown(...args);
            tableMoveColumnRowHandlers.beforeOnCellMouseDown(...args);
        },
        afterSelection: tableCellSelectionHandlers.afterSelection,
        afterSelectionEnd: tableCellSelectionHandlers.afterSelectionEnd,
        afterDeselect: tableCellSelectionHandlers.afterDeselect,
        [GRID_SHORTCUTS_PLUGIN_NAME]: true,
        [EDITOR_SHORTCUTS_PLUGIN_NAME]: true,
        [CONTEXT_MENU_SHORTCUTS_PLUGIN_NAME]: true,
        [COPY_PASTE_EXTENSION_PLUGIN_NAME]: true,
        [GRID_UPDATE_PLUGIN_NAME]: true,
        [FORMULA_TYPE_INFERENCE_PLUGIN_NAME]: true,
        [EDITING_CLEANUP_PLUGIN_NAME]: true,

        // Switch between DraftJS and non-DraftJS formula plugins based on the editor type
        // TODO-TABLES: Remove this once DraftJS is fully deprecated
        ...(isDraftJs
            ? {
                  [DRAFTJS_FORMULA_CELL_REFERENCE_PLUGIN_NAME]: true,
                  [DRAFTJS_FORMULA_AUTOCOMPLETE_PLUGIN_NAME]: {
                      dropdownRef,
                  },
              }
            : {
                  [FORMULA_CELL_REFERENCE_PLUGIN_NAME]: true,
                  [FORMULA_AUTOCOMPLETE_PLUGIN_NAME]: {
                      dropdownRef,
                  },
              }),

        [CONTEXT_MENU_EXTENSION_PLUGIN_NAME]: true,
        [EDITING_PLUGIN_NAME]: true,
    };
};

/**
 * Cleans up the hot instance while giving React time to finish unmounting dependants.
 */
const destroyHotTableInstance = (hotTableInstance: MNHotInstance | null) => {
    // HOT CopyPaste plugin add paste event listeners on `document`.
    // When a late paste event occurs after Handsontable instance is destroyed,
    // this could cause an error as it will try to unsafely access `getActiveEditor()` from hotInstance.
    hotTableInstance?.getPlugin('copyPaste')?.disablePlugin();

    setTimeout(() => {
        hotTableInstance?.destroy();
    });
};

const createHotTableInstance = (hotTableContainer: HTMLDivElement, args: UseInitializeHotTableArgs): MNHotInstance => {
    const settings = createHotTableSettings(args);

    const instance = new Handsontable(hotTableContainer, settings) as MNHotInstance;

    // Update the instance with our extended props.
    // (We can't do this by extending the Handsontable class, as it's actually a factory
    // method that returns an instance rather than a class that can be extended)
    instance.getMNEditor = instance.getActiveEditor as MNHotInstance['getMNEditor'];

    return instance;
};

/**
 * Initialises and tears down the Handsontable instance for the current table.
 */
const useInitializeHotTable = (args: UseInitializeHotTableArgs) => {
    const {
        elementId,
        tableContentData,
        tableContentColWidthsGU,
        hotTableInstanceRef,
        hotTableContainerRef,
        gridSize,
        isDarkMode,
        isResizing,
        isReadOnly,
        showTitle,
        showBottomPanel,
        locale,
        undoRedoRichCellDataState,
        getContextZoomScale,
        startEditingCellEditor,
        finishEditingCellEditor,
        jumpToCaption,
        jumpToTitle,
        cellEditorRef,
    } = args;
    const [mounted, setMounted] = useState(false);

    /**
     * 1. Initialise Handsontable instance
     */
    useEffect(() => {
        if (!hotTableContainerRef.current) return;

        const inst = createHotTableInstance(hotTableContainerRef.current, args);

        hotTableInstanceRef.current = inst;

        return () => {
            destroyHotTableInstance(inst);
        };
    }, []);

    /**
     * 2. Sync some Milanote-specific props to hotInstance.milanoteProps so that they can be used in plugins
     */
    useSyncHotMilanoteProps({
        hotTableInstanceRef,
        milanoteProps: {
            gridSize,
            isDarkMode,
            showTitle,
            showBottomPanel,
            getContextZoomScale,
            hotTableContainerRef,
            isResizing,
            elementId,
            locale,
            undoRedoRichCellDataState,
            jumpToCaption,
            jumpToTitle,
            isReadOnly,
            startEditingCellEditor,
            finishEditingCellEditor,
            cellEditorRef,
        },
    });

    /**
     *  3. Enhance Handsontable with richer metadata
     */
    useEffect(() => {
        const hotInstance = hotTableInstanceRef.current;
        if (!hotInstance) return;

        const initialTableContent = {
            data: tableContentData,
            colWidthsGU: tableContentColWidthsGU,
        };

        // We need to initialise the cell meta objects & formula plugins of the new handsontable instance
        applyRawTableContentToHotInstance(hotInstance, initialTableContent, gridSize, 'load');

        // HOT starts with an empty undo stack, so remove HyperFormula's mount-time sheet setup history
        const formulasPlugin = hotInstance.getPlugin<'formulas'>('formulas');
        // just checking if it's defined
        if (isNumber(formulasPlugin.sheetId)) {
            removeHyperFormulaUndoRedoEntriesForSheet(hotInstance, formulasPlugin.sheetId);
        }

        setMounted(true);
    }, []);

    /**
     * 4. Force render after table has fully mounted. This will ensure that the row ref headers heights are correct.
     */
    useEffect(() => {
        if (!mounted) return;

        hotTableInstanceRef.current?.render();
    }, [mounted]);

    return mounted;
};

export default useInitializeHotTable;
